<template>
  <section
    id="checkoutPayment"
    class="open expandable_section bottom-checkout-column"
  >
    <h2
      class="expandable_header"
    >
      {{ $t("checkout.payment.paymentMethod") }}
    </h2>

    <div class="payment-errors">
      <!-- Form -->
      <div
        v-if="protectGroupMissing && paymentErrors"
        @click="revealError('protect-group')"
      >
        <span class="error">{{ $t("checkout.payment.selectRefundable") }}</span>
      </div>
      <div
        v-if="billingInfoMissing && paymentErrors"
        @click="revealError('address')"
      >
        <span class="error">{{ missingBillingItem }} is required</span>
      </div>
      <div v-if="coveredByCredit">
        <button
          class="continue-button btn btn-primary"
          @click="placeOrder(null)"
        >
          {{ placeOrderText }}
        </button>
      </div>
      <div
        v-else
        :class="[validToPay, 'expandable_content', 'review-payment']"
        @click="confirmPayable"
      >
        <p
          id="checkoutError"
          :class="[errs == null ? '' : 'errors']"
          @click="handleError(errs)"
        >
          {{ errs }}
        </p>
        <p
          v-if="loginErr"
          id="loginError"
          class="errors"
          @click="retryLogin"
        >
          {{ $t('checkout.payment.preexistingAccount') }}
        </p>

        <StripeForm
          :address="billing.address1"
          :address2="billing.address2"
          :buttontext="placeOrderText"
          :city="billing.city"
          :name="name"
          :phone="billing.phoneNumber"
          :state="billing.state"
          :zipcode="billing.zipCode"
          :order="order"
          :errs="errs"
          includechecks="true"
          @card-added="placeOrder"
          @update-terms="updateTerm"
          @update-f1="updateF1"
        />
      </div>
      <div
        v-if="card.id != undefined"
        class="processing-payment loading"
      >
        <div class="loading-icon" />

        <button
          disabled
          class="continue-button continue-button btn btn-primary"
        >
          {{ $t("checkout.payment.processing",
                { amount: paymentfields.amountDecorated, numbers: card.last_4 }) }}
        </button>
      </div>
    </div>
  </section>
</template>

<script>
/* global ga pi Turbolinks */

import Api from '../cart/cart_api'
import CountryData from '../country_data'
import StripeForm from '../stripe_form/credit_card_form.vue'
import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: {
    StripeForm
  },

  props: {
    number: {
      default: null,
      required: false,
      type: Number
    },
    order: {
      default: null,
      required: false,
      type: Object
    }
  },

  data() {
    return {
      accepted_terms: false,
      card: {},
      errs: null,
      f1Consent: false,
      pardot_id: '',
      sectionName: 'payment',
      loadingContent: false,
      loginErr: false,
      payableErrors: false
    }
  },

  computed: {
    ...mapState('checkout', {
      paymentfields: state => state.paymentFields,
      billing: state => state.billing,
      isGuest: state => state.isGuest,
      protectGroupDecisionMade: state => state.protectGroupDecisionMade
    }),
    amountMissing() {
      return this.paymentfields.amount == undefined ||
             this.paymentfields.amount == null ||
            (this.paymentfields.amount < 1 && !this.$store.state.checkout.applyCredit)
    },
    paymentErrors() {
      return this.payableErrors
    },
    missingBillingItem(){
      let missingItems = []
      this.billing.firstName ? null : missingItems.push('First name')
      this.billing.lastName ? null : missingItems.push('Last name')
      this.billing.phoneNumber ? null : missingItems.push('Phone')
      this.billing.address1 ? null : missingItems.push('Address')
      this.billing.city ? null : missingItems.push('City')
      this.billing.state ? null : missingItems.push('State')
      this.billing.country ? null : missingItems.push('Country')
      this.billing.zipCode ? null : missingItems.push('Zip Code')

      return missingItems.join(', ')
    },
    billingInfoMissing() {
      return jQuery.isEmptyObject(this.billing.firstName) ||
             jQuery.isEmptyObject(this.billing.lastName) ||
             jQuery.isEmptyObject(this.billing.phoneNumber) ||
             jQuery.isEmptyObject(this.billing.address1) ||
             jQuery.isEmptyObject(this.billing.city) ||
             this.billing.state == 'missing state value' ||
             jQuery.isEmptyObject(this.billing.city) ||
             this.invalidCountry ||
             this.invalidPostalCode
    },

    protectGroupMissing() {
      return !this.protectGroupDecisionMade
    },

    creditsUsed(){
      if(this.$store.state.checkout.applyCredit){
        return Math.min(this.$store.state.checkout.creditAmount,this.$store.state.checkout.orderTotal)
      }

      return 0
    },

    coveredByCredit() {
      if(this.$store){
        return this.order.credit_amount + 100 >= this.order.total
      }

      return false
    },

    formData() {
      return {
        checkout: {
          accepted_terms: this.coveredByCredit ? true : this.accepted_terms,
          address: this.billing.address1,
          address2: this.billing.address2,
          amount: this.paymentfields.amount,
          card_id: this.card.id,
          city: this.billing.city,
          company_name: this.billing.companyName,
          country: this.billing.country,
          paid_by_credits: this.coveredByCredit,
          credits: this.creditsUsed,
          f1_consent: this.f1Consent,
          first_name: this.billing.firstName,
          ga_id: this.gaId,
          gcl_id: window.localStorage.gclid,
          last_name: this.billing.lastName,
          order_id: this.paymentfields.orderId,
          pardot_id: this.pardotID(),
          phone_number: this.billing.phoneNumber,
          purchase_order_number: this.billing.purchaseOrderNumber,
          room_information: this.paymentfields.roomInformation,
          state: this.billing.state,
          zip_code: this.billing.zipCode
        }
      }
    },

    gaId() {
      if (typeof ga === 'function') {
        return ga(function(tracker) { return tracker.get('clientId') } )
      } else {
        return null
      }
    },

    invalidCountry() {
      return CountryData.blockedCountries().concat(
        ['', null, undefined]).includes(this.billing.country)
    },

    invalidPostalCode() {
      if (['CA', 'US'].includes(this.billing.country)) {
        return jQuery.isEmptyObject(this.billing.zipCode)
      } else {
        // We only require code of US and Canada so empty zip is valid in all other cases
        return false
      }
    },

    name() {
      return `${this.billing.firstName} ${this.billing.lastName}`
    },

    placeOrderText() {
      return this.$t('checkout.payment.placeOrder', { amount: '' })
    },

    valid() {
      return this.card.id == undefined &&
             !this.amountMissing &&
             !this.billingInfoMissing &&
             this.protectGroupDecisionMade
    },
    validToPay(){
      return (!this.amountMissing &&
             !this.billingInfoMissing &&
             this.protectGroupDecisionMade) ? '': 'disabled'
    },

    isGuestAccount() {
      let el = document.querySelector('#accountStatus')
      return el.dataset.guest == true
    }
  },

  methods: {
    ...mapActions('checkout', ['updateSection']),
    ...mapMutations('checkout', ['updatePaymentAttempted']),
    isEmpty(obj) {
      for(var key in obj) {
        // eslint-disable-next-line no-prototype-builtins
        if(obj.hasOwnProperty(key))
          return false
      }
      return true
    },
    revealError(target){
      this.updateSection(target)
    },
    confirmPayment(paymentTermId, paymentIntentId) {
      let body = JSON.stringify({ checkout: {
        f1_consent: this.f1Consent,
        payment_intent_id: paymentIntentId,
        payment_term_id: paymentTermId,
      }})

      Api.checkoutConfirm(body).then(order => {
        if (order.redirect) {
          Turbolinks.visit(order.redirect)
        } else if (order.error) {
          this.card = {}
          this.errs = order.error
        } else {
          this.card = {}
          this.errs = this.$t('checkout.payment.couldNotProcess')
        }
      })
    },

    handleError(errs) {
      if(errs == this.$t('checkout.payment.hotelNotAvailable')) {
        Api.getOrder().then(order=>{
          location.pathname=`cart/items/${order.items[0].id}/hotels`
        })
      } else{
        errs=null
      }
    },

    handleSCA(order) {
      this.card.stripe.paymentIntentSCA(order.client_secret).then(result => {
        if (result.error) {
          this.card = {}
          this.errs = result.error.message
        } else {
          this.confirmPayment(order.payment_term_id, result.paymentIntent.id)
        }
      })
    },

    placeOrder(card) {
      if (!this.valid) { return }
      card ? this.placeOrderWithCard(card) : this.placeOrderWithCredit()
    },

    placeOrderWithCredit(){
      Api.checkout(JSON.stringify(this.formData)).then(order => {
        if (order.redirect) {
          window.location.replace(order.redirect)
        } else if (order.client_secret) {
          this.handleSCA(order)
        } else if (order.error) {
          this.errs = order.error
        } else {
          this.errs = this.$t('checkout.payment.couldNotProcess')
        }
      })
    },

    async placeOrderWithCard(card) {
      this.card = card
      if(this.isGuest == 'true') {
        let response = await Api.updateGuestUser(this.$store.state.checkout.emailAddress)
        if (response.ok) {
          this.updatePaymentAttempted(true)
          this.processPayment()
        } else {
          this.card = {}
          this.loginErr = true
        }
      } else {
        this.processPayment()
      }
    },
    confirmPayable(){
      if(!this.valid){
        this.payableErrors = true
      }
    },
    retryLogin() {
      this.updateSection('address')
      this.loginErr = false
    },

    pardotID() {
      try {
        return pi.tracker.visitor_id
      } catch (_) {
        null
      }
    },

    updateF1(bool) {
      this.f1Consent = bool
    },

    updateTerm(bool) {
      this.accepted_terms = bool
    },

    processPayment() {
      Api.checkout(JSON.stringify(this.formData)).then(order => {
        if (order.redirect) {
          window.location.replace(order.redirect)
        } else if (order.client_secret) {
          this.handleSCA(order)
        } else if (order.error) {
          this.card = {}
          this.errs = order.error
        } else {
          this.card = {}
        }
      })
    },
  }
}
</script>
